//General Snapping Utils

import * as Globals from "../globals";
import {getClosestBoundaryLine, getPointsFromBoundingBox} from "./threeUtils";
import {mousePosition} from "../main";
import {getWallGroupParent} from "./configuratorUtils";
import {Box3, Vector3} from "three";
import {toFixedFloat} from "./utils";
import {getWallOrientation} from "../models/walls/wall";

/* Snappen an Geometrie Bounds über alle relevanten Geometrien nicht nur die aktuell geschnittene.
 * Gibt den Geometriepunkt zurück mit der kürzesten Distanz zur Maus.
 */
export function snapPointToGeometryBounds(meshes) {
    let snapPoint = null;
    let snapObject = null;
    let distance = null;
    
    if (meshes.length > 0) {
        for (let i = 0; i < meshes.length; i++) {
            let element = meshes[i];
            let bounds = new Box3().setFromObject(element);
            let newSnapPoint = snapPointToBoundsVertex(mousePosition, bounds, true); //Obersten vier Punkte der Bounds auslesen
            //Snappen an den Punkt mit geringster Distanz zur Maus. Für jedes Element berechnen > Insgesamt nähester Punkt. Verhindert springen bei Mausbewegung über Elemente
            if (snapPoint === null) {
                snapPoint = newSnapPoint;
                snapObject = element;
                distance = mousePosition.distanceTo(newSnapPoint);
            } else {
                let tmpDistance = mousePosition.distanceTo(newSnapPoint);
                if (tmpDistance < distance) {
                    snapPoint = newSnapPoint;
                    snapObject = element;
                    distance = tmpDistance;
                }
            }
        }
    }

    return {snapPoint: snapPoint, snapObject: snapObject, distance: distance};
}

//Snappen eines Punkts an die Bounds eines einzelnen Objekts. Über singleHeight kann festgelegt werden das diese in einer Ebene liegen > Nur Y Max der Bounds
//TODO: ggf. erweitern auf min oder max Punkte oder beides statt singleheight
export function snapPointToBoundsVertex(startPoint, bounds, singleHeight = false) {
    //Find the closest vertex from bounds to startPoint(mouse position)
    let nearestPoint, nearestDistance;
    let points = getPointsFromBoundingBox(bounds, singleHeight);

    for (let i = 0; i < points.length; i++) {
        if (i === 0) {
            nearestPoint = points[i];
            nearestDistance = startPoint.distanceTo(points[i]);
        }

        let distance = startPoint.distanceTo(points[i]);
        if (distance < nearestDistance) {
            nearestDistance = distance;
            nearestPoint = points[i];
        }
    }

    return nearestPoint;
}

// snaps the given point to the closest grid point
export function snapPointToGrid(point) {
    let elements = Math.round(point.x / Globals.HBESectionWidth);
    point.x = elements * Globals.HBESectionWidth;
    elements = Math.round(point.y / Globals.HBESectionWidth);
    point.y = elements * Globals.HBESectionWidth;
    elements = Math.round(point.z / Globals.HBESectionWidth);
    point.z = elements * Globals.HBESectionWidth;
    return point;
}

// snaps point into corner of grid. Offset by half the elements size to fit correctly
export function snapPointToGridCorners(position, sizeX, sizeZ) {
    let gridPosition = new Vector3();
    gridPosition.copy(position);

    gridPosition = snapPointToGrid(gridPosition);

    let snapOffsetX = sizeX / 2;
    let snapOffsetZ = sizeZ / 2;
    let offset;
    //Check in which corner to snap the point
    if (position.x < gridPosition.x && position.z < gridPosition.z) {
        offset = new Vector3(-snapOffsetX, 0, -snapOffsetZ);
        position = gridPosition.add(offset);
    } else if (position.x > gridPosition.x && position.z < gridPosition.z) {
        offset = new Vector3(snapOffsetX, 0, -snapOffsetZ);
        position = gridPosition.add(offset);
    } else if (position.x < gridPosition.x && position.z > gridPosition.z) {
        offset = new Vector3(-snapOffsetX, 0, snapOffsetZ);
        position = gridPosition.add(offset);
    } else if (position.x > gridPosition.x && position.z > gridPosition.z) {
        offset = new Vector3(snapOffsetX, 0, snapOffsetZ);
        position = gridPosition.add(offset);
    }

    // return {position: position, offset: offset};
    return position;
}

export function snapPointToClosestWallBoundary(position, meshes, offset = 0.5 * Globals.HBEWallDefaultThickness) {
    if (meshes.length === 0) {
        return null;
    }

    let point = new Vector3(position.x, 0, position.z); //Ausgangspunkt

    let closestBoundaryObject = getClosestBoundaryLine(meshes);
    let closestElement = closestBoundaryObject.element;
    let closestLine = closestBoundaryObject.line;
    if (closestElement === null || closestLine === null) {
        return null;
    }

    let closestWallGroup = getWallGroupParent(closestElement);
    if (!closestWallGroup) {
        closestWallGroup = closestElement;
    }

    let closestWallOrientation = getWallOrientation(closestWallGroup);
    let lineVector = new Vector3().subVectors(closestLine.end, closestLine.start);
    let lineDirection = Math.abs(lineVector.x) > Math.abs(lineVector.z) ? "x" : "z";
    let lineAndWallDirectionEqual = lineDirection === closestWallOrientation;

    if (lineAndWallDirectionEqual) {
        //Punkte Position setzen: Koordinate entgegen der Wandrichtung an die gesnappt wird
        if (closestWallOrientation === "x") {
            point.setZ(position.z > closestLine.start.z ? closestLine.start.z + offset : closestLine.start.z - offset);
        } else {
            point.setX(position.x > closestLine.start.x ? closestLine.start.x + offset : closestLine.start.x - offset);
        }

        if (closestWallOrientation === "x") {
            if (Math.abs(closestLine.start.x - position.x) <= 0.1) {
                let snapAmount = closestLine.start.x < closestLine.end.x ? offset : -offset;
                point.setX(closestLine.start.x + snapAmount);
            } else if (Math.abs(closestLine.end.x - position.x) <= 0.1) {
                let snapAmount = closestLine.start.x < closestLine.end.x ? -offset : offset;
                point.setX(closestLine.end.x + snapAmount);
            }
        } else {
            if (Math.abs(closestLine.start.z - position.z) <= 0.1) {
                let snapAmount = closestLine.start.z < closestLine.end.z ? offset : -offset;
                point.setZ(closestLine.start.z + snapAmount);
            } else if (Math.abs(closestLine.end.z - position.z) <= 0.1) {
                let snapAmount = closestLine.start.z < closestLine.end.z ? -offset : offset;
                point.setZ(closestLine.end.z + snapAmount);
            }
        }
    } else if (toFixedFloat(lineVector.length()) === Globals.HBEWallDefaultThickness) {
        //Punkte Position setzen: Koordinate entgegen der Wandrichtung an die gesnappt wird
        let closestWallBounds = new Box3().setFromObject(closestWallGroup);
        let closestWallCenter = closestWallBounds.getCenter(new Vector3());
        if (closestWallOrientation === "x") {
            point.setZ(closestWallCenter.z);
        } else {
            point.setX(closestWallCenter.x);
        }

        if (closestWallOrientation === "x") {
            if (position.x > closestWallBounds.max.x) {
                point.setX(closestWallBounds.max.x + offset);
            } else if (mousePosition.x < closestWallBounds.min.x) {
                point.setX(closestWallBounds.min.x - offset);
            }
        } else {
            if (position.z > closestWallBounds.max.z) {
                point.setZ(closestWallBounds.max.z + offset);
            } else if (position.z < closestWallBounds.min.z) {
                point.setZ(closestWallBounds.min.z - offset);
            }
        }
    }

    return point;
}


export function snapPointIntoWallBoundary(position, meshes, offset = 0.5 * Globals.HBEWallDefaultThickness, verticalOffset = 0) {
    if (meshes.length === 0) {
        return null;
    }

    let point = new Vector3(mousePosition.x, 0, mousePosition.z); //Ausgangspunkt

    let closestBoundaryObject = getClosestBoundaryLine(meshes);
    let closestElement = closestBoundaryObject.element;
    let closestLine = closestBoundaryObject.line;
    if (closestElement === null || closestLine === null) {
        return null;
    }

    let closestWallGroup = getWallGroupParent(closestElement);
    if (!closestWallGroup) {
        closestWallGroup = closestElement;
    }

    let closestWallOrientation = getWallOrientation(closestWallGroup);
    let bounds = new Box3().setFromObject(closestWallGroup);
    let center = bounds.getCenter(new Vector3());

    //Punkte Position setzen: Koordinate entgegen der Wandrichtung an die gesnappt wird

    if (closestWallOrientation === "x") {
        if (Math.abs(closestLine.start.x - mousePosition.x) <= 0.1) {
            let snapAmount = closestLine.start.x < closestLine.end.x ? offset : -offset;
            point.setX(closestLine.start.x + snapAmount);
        } else if (Math.abs(closestLine.end.x - mousePosition.x) <= 0.1) {
            let snapAmount = closestLine.start.x < closestLine.end.x ? -offset : offset;
            point.setX(closestLine.end.x + snapAmount);
        }
    } else {
        if (Math.abs(closestLine.start.z - mousePosition.z) <= 0.1) {
            let snapAmount = closestLine.start.z < closestLine.end.z ? offset : -offset;
            point.setZ(closestLine.start.z + snapAmount);
        } else if (Math.abs(closestLine.end.z - mousePosition.z) <= 0.1) {
            let snapAmount = closestLine.start.z < closestLine.end.z ? -offset : offset;
            point.setZ(closestLine.end.z + snapAmount);
        }
    }

    if (closestWallOrientation === "x") {
        if (mousePosition.z > center.z) {
            point.setZ(center.z + verticalOffset);
        } else {
            point.setZ(center.z - verticalOffset);
        }
    } else {
        if (mousePosition.x > center.x) {
            point.setX(center.x + verticalOffset);
        } else {
            point.setX(center.x - verticalOffset);
        }
    }

    return point;
}

// Along-axis magnet while drawing. Wide enough to catch a section-grid miss (half of 0.545 is ~0.27)
// without jumping to a wall the cursor is not actually approaching.
export const SNAP_AXIS_TOLERANCE = 0.3;
export const SNAP_CLOSE_TOLERANCE = 0.45;
export const SNAP_MIN_SEGMENT = 0.2;
export const SNAP_MIN_HIT = 0.12;
// A beam running just outside a wall still has to lock to that wall's face.
export const SNAP_JOIN_LATERAL = 0.55;
// How close the free end must be before it locks onto a wall or beam face.
export const SNAP_JOIN_ALONG = 0.9;
// Pull onto a parallel wall or beam only when the cursor is already on that joint.
export const SNAP_ALIGN_LATERAL = 0.22;
// Seat a butt joint this far into the face so the two walls actually meet.
export const SNAP_BUTT_OVERLAP = 0.01;

function lateralGap(value, a, b) {
    const min = Math.min(a, b);
    const max = Math.max(a, b);
    if (value >= min && value <= max) {
        return 0;
    }
    return Math.min(Math.abs(value - min), Math.abs(value - max));
}

// Closest constant-x or constant-z face line. Distance is absolute, so the nearest face wins.
// lateralSlop limits the match to a face the point is actually beside, so a wall that
// does not reach this spot cannot pull the start onto its infinite line.
export function nearestAxisCoordinate(point, lines, axis, tolerance = SNAP_AXIS_TOLERANCE, lateralSlop = null) {
    if (!point || !lines || lines.length === 0) {
        return null;
    }

    const other = axis === "x" ? "z" : "x";
    let best = null;
    for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        const coord = axis === "x" ? line.start.x : line.start.z;
        const distance = Math.abs((axis === "x" ? point.x : point.z) - coord);
        if (distance > tolerance) {
            continue;
        }
        if (lateralSlop !== null) {
            const gap = lateralGap(point[other], line.start[other], line.end[other]);
            if (gap > lateralSlop) {
                continue;
            }
        }
        if (best === null || distance < best.distance) {
            best = {coord, distance, line};
        }
    }

    return best;
}

/**
 * Pull a segment end onto the wall face the cursor is about to meet.
 * A face the segment actually crosses wins over a face it only lines up with,
 * so a pillar, a beam end, or a wall off to the side cannot stop it short.
 * Among real hits, the first wall on the path wins. `prefer: "near"` stops on
 * that face. `prefer: "far"` continues across that wall's thickness.
 * `prefer: "flush"` stops on the near face of a wall the segment hits, and on the
 * far face when it only runs beside that wall.
 */
export function snapEndToFace(origin, rawEnd, axis, lines, options = {}) {
    if (!origin || !rawEnd || !lines || lines.length === 0) {
        return null;
    }

    const tolerance = options.tolerance ?? SNAP_CLOSE_TOLERANCE;
    const lateralTolerance = options.lateralTolerance ?? Globals.HBEWallDefaultThickness * 1.5;
    const cluster = options.cluster ?? Globals.HBEWallDefaultThickness + 0.02;
    const prefer = options.prefer ?? "near";
    const minSegment = options.minSegment ?? SNAP_MIN_SEGMENT;
    const hitSlop = options.hitSlop ?? 0.02;
    const minWallFace = options.minWallFace ?? 0.2;
    const preferKind = options.preferKind ?? null;
    const other = axis === "x" ? "z" : "x";
    const originCoord = origin[axis];
    const rawCoord = rawEnd[axis];
    const forward = rawCoord >= originCoord ? 1 : -1;
    const candidates = [];

    for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        const coord = line.start[axis];
        const along = (coord - originCoord) * forward;
        if (along < minSegment) {
            continue;
        }
        const delta = Math.abs(rawCoord - coord);
        if (delta > tolerance) {
            continue;
        }
        const gap = lateralGap(rawEnd[other], line.start[other], line.end[other]);
        if (gap > lateralTolerance) {
            continue;
        }
        const span = Math.abs(line.start[other] - line.end[other]);
        candidates.push({
            coord,
            along,
            delta,
            gap,
            span,
            kind: line.snapKind || "other",
            hit: gap <= hitSlop && span >= minWallFace
        });
    }

    if (candidates.length === 0) {
        return null;
    }

    let hits = candidates.filter((candidate) => candidate.hit);
    if (preferKind && hits.some((candidate) => candidate.kind === preferKind)) {
        hits = hits.filter((candidate) => candidate.kind === preferKind);
    }

    let pool = hits;
    if (pool.length === 0) {
        const longFaces = candidates.filter((candidate) => candidate.span >= minWallFace);
        pool = longFaces.length > 0 ? longFaces : candidates;
    }

    // A wall on the path is taken in order, so the segment cannot tunnel through it.
    // A face the segment misses is only an alignment guide, and the cursor decides which.
    if (hits.length > 0) {
        pool.sort((a, b) => a.along - b.along || a.delta - b.delta);
    } else {
        pool.sort((a, b) => a.delta - b.delta || a.along - b.along);
    }
    const anchor = pool[0];
    let chosen = anchor;
    let mode = prefer;
    if (prefer === "flush") {
        mode = hits.length > 0 || anchor.gap <= 0.02 ? "near" : "far";
    } else if (prefer === "choice") {
        // Both faces of the wall, one thickness apart. The cursor picks which one.
        mode = "nearest";
    }

    // Stay on the same wall: the opposite face is one thickness away from the face the cursor reached.
    for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        if (hits.length > 0 && preferKind && line.snapKind && line.snapKind !== preferKind) {
            continue;
        }
        const coord = line.start[axis];
        if (Math.abs(coord - anchor.coord) > cluster) {
            continue;
        }
        const along = (coord - originCoord) * forward;
        if (along < minSegment) {
            continue;
        }
        const gap = lateralGap(rawEnd[other], line.start[other], line.end[other]);
        if (gap > (hits.length > 0 ? hitSlop : lateralTolerance)) {
            continue;
        }
        const span = Math.abs(line.start[other] - line.end[other]);
        if (hits.length > 0 && span < minWallFace) {
            continue;
        }
        if (mode === "far" && along > chosen.along) {
            chosen = {coord, along};
        } else if (mode === "near" && along < chosen.along) {
            chosen = {coord, along};
        } else if (mode === "nearest" && Math.abs(rawCoord - coord) < Math.abs(rawCoord - chosen.coord) - 0.0005) {
            chosen = {coord, along};
        }
    }

    const overlap = mode === "near" ? (options.overlap ?? 0) : 0;
    const point = rawEnd.clone();
    point[axis] = chosen.coord + forward * overlap;
    point[other] = origin[other];
    return point;
}

// Pull the open start back onto the wall it is leaving, then seat it slightly into that face.
export function snapStartEdge(originCoord, lateral, forward, axis, lines, options = {}) {
    if (!lines || lines.length === 0 || !forward) {
        return null;
    }

    const tolerance = options.tolerance ?? SNAP_AXIS_TOLERANCE;
    const hitSlop = options.hitSlop ?? Globals.HBEWallDefaultThickness + 0.15;
    const minWallFace = options.minWallFace ?? 0.2;
    const overlap = options.overlap ?? 0;
    const other = axis === "x" ? "z" : "x";
    let best = null;

    for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        if (options.preferKind && line.snapKind && line.snapKind !== options.preferKind) {
            continue;
        }
        const coord = line.start[axis];
        const along = (coord - originCoord) * forward;
        // A face just ahead still counts: the start can sit inside the wall, and the face
        // to meet is the one the segment leaves through, not the outer face behind it.
        if (along > Globals.HBEWallDefaultThickness || along < -tolerance) {
            continue;
        }
        const gap = lateralGap(lateral, line.start[other], line.end[other]);
        if (gap > hitSlop) {
            continue;
        }
        const span = Math.abs(line.start[other] - line.end[other]);
        if (span < minWallFace) {
            continue;
        }
        const distance = Math.abs(along);
        const closer = best === null || distance < best.distance - 0.001;
        const sameSpotFurtherAlong = best !== null && Math.abs(distance - best.distance) <= 0.001 && along > best.along;
        if (closer || sameSpotFurtherAlong) {
            best = {coord, distance, along};
        }
    }

    if (best === null) {
        return null;
    }

    return best.coord - forward * overlap;
}

// Center of a parallel wall or beam. Short pillar faces are ignored so a corner post
// cannot pull the segment off the wall it is supposed to continue.
export function parallelCenterline(coord, lines, axis, tolerance = SNAP_ALIGN_LATERAL) {
    if (coord === undefined || coord === null || !lines || lines.length === 0) {
        return null;
    }

    const other = axis === "x" ? "z" : "x";
    const faces = [];
    for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        const faceCoord = line.start[axis];
        const length = Math.abs(line.start[other] - line.end[other]);
        let existing = null;
        for (let j = 0; j < faces.length; j++) {
            if (Math.abs(faces[j].coord - faceCoord) < 0.001) {
                existing = faces[j];
                break;
            }
        }
        if (existing) {
            existing.length = Math.max(existing.length, length);
        } else {
            faces.push({coord: faceCoord, length: length});
        }
    }

    faces.sort((a, b) => a.coord - b.coord);
    let best = null;
    for (let i = 0; i < faces.length; i++) {
        for (let j = i + 1; j < faces.length; j++) {
            const span = faces[j].coord - faces[i].coord;
            if (span < 0.04 || span > 0.35) {
                continue;
            }
            const spanLength = Math.min(faces[i].length, faces[j].length);
            if (spanLength < 0.25) {
                continue;
            }
            const center = (faces[i].coord + faces[j].coord) / 2;
            const distance = Math.abs(coord - center);
            if (distance > tolerance) {
                continue;
            }
            if (best === null || distance < best.distance) {
                best = {center, distance};
            }
        }
    }

    return best ? best.center : null;
}

export function snapToBoundaryLines(elements) {
    let x = getClosestBoundaryLine(elements);
    let closestElement = x.element;
    let closestLine = x.line;
    let closestDistance = x.distance;

    if (closestDistance !== null) {
        let wallGroup = getWallGroupParent(closestElement);
        let orientation = getWallOrientation(wallGroup);

        if (orientation === "x") {
            return new Vector3(mousePosition.x, 0, closestLine.start.z);
        } else {
            return new Vector3(closestLine.start.x, 0, mousePosition.z);
        }
    }

    return null;
}
